/* Atlas V3 Desktop — ticker state + right-dock panel provider.
   Desktop-only replacement for the mobile StockDetailProvider: same state model
   (openTicker guard, ?stock= deep-link, snapshot+historical price merge) but it
   renders the right-side TickerPanel instead of the mobile bottom sheet — so the
   mobile sheet stays 100% untouched. Provides openTicker via TickerCtx so every
   page/atom (useTicker) opens the dock. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { TickerCtx } from '@/v2/v3/lib'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { TickerPanel } from './TickerPanel'

const Ctx = createContext(null)
export function useTickerPanel() {
  return useContext(Ctx) || { openTicker: () => {}, close: () => {}, hasTicker: () => null, openSymbol: null }
}

function readUrlTicker() {
  if (typeof window === 'undefined') return null
  const t = new URLSearchParams(window.location.search).get('stock')
  return t ? t.toUpperCase() : null
}
function pushUrlTicker(ticker) {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (ticker) url.searchParams.set('stock', ticker)
  else url.searchParams.delete('stock')
  window.history.pushState({}, '', url.toString())
}

export function TickerPanelProvider({ children, extraStocks = [] }) {
  const { stocks } = useAtlasStocks()
  const [sym, setSym] = useState(() => readUrlTicker())

  const focus = useMemo(() => (sym ? [sym] : []), [sym])
  const { prices: snap } = useLivePrices(focus)
  const { prices: hist } = useLivePrices(focus, 0, 'historical')
  const live = useMemo(() => {
    if (!sym) return {}
    return { ...hist[sym], ...snap[sym] } // snapshot wins on shared fields
  }, [snap, hist, sym])

  const merged = useMemo(() => {
    if (!extraStocks?.length) return stocks
    const seen = new Set(stocks.map((s) => s.ticker))
    const extra = extraStocks.filter((s) => s?.ticker && !seen.has(s.ticker))
    return extra.length ? [...stocks, ...extra] : stocks
  }, [stocks, extraStocks])

  const stock = useMemo(() => (sym ? merged.find((s) => s.ticker === sym) ?? null : null), [sym, merged])

  const openTicker = useCallback((t) => {
    if (!t) return
    const up = String(t).toUpperCase()
    if (!merged.some((s) => s.ticker === up)) {
      console.warn('[TickerPanel] openTicker(' + up + ') skipped — not in universe')
      return
    }
    setSym(up); pushUrlTicker(up)
  }, [merged])

  const close = useCallback(() => { setSym(null); pushUrlTicker(null) }, [])

  // Membership probe — visible-feedback callers (Agent Desk) ask before
  // opening. true = will open; false = not in universe; null = still loading.
  const hasTicker = useCallback((t) => {
    if (!t) return false
    if (merged.length === 0) return null
    const up = String(t).toUpperCase()
    return merged.some((s) => s.ticker === up)
  }, [merged])

  // Esc closes the dock — but only when no layer sits above it (⌘K palette,
  // news/pick reader sheets), so each press dismisses exactly one layer.
  useEffect(() => {
    if (!sym) return
    const h = (e) => {
      if (e.key !== 'Escape') return
      if (document.querySelector('.v3web-cmdk') || document.querySelector('[data-v3web-sheet]')) return
      close()
    }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [sym, close])
  useEffect(() => {
    const h = () => setSym(readUrlTicker())
    window.addEventListener('popstate', h)
    return () => window.removeEventListener('popstate', h)
  }, [])
  useEffect(() => {
    if (!sym || merged.length === 0) return
    if (!merged.some((s) => s.ticker === sym)) { setSym(null); pushUrlTicker(null) }
  }, [sym, merged])

  // Shift the main content when the dock is open (CSS reads this attr).
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.dataset.v3webPanel = stock ? 'open' : ''
    return () => { document.documentElement.dataset.v3webPanel = '' }
  }, [stock])

  const value = useMemo(() => ({ openTicker, close, hasTicker, openSymbol: sym }), [openTicker, close, hasTicker, sym])

  return (
    <Ctx.Provider value={value}>
      <TickerCtx.Provider value={openTicker}>
        {children}
        {stock && <TickerPanel stock={stock} live={live} onClose={close} />}
      </TickerCtx.Provider>
    </Ctx.Provider>
  )
}
